import { ApplicationRef, Injectable, OnDestroy } from '@angular/core'
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
import { EventService } from '@spartacus/core'
import { MiVideo } from '../../cms/services/moving-image/moving-image'
import { Image, TextInput } from '../../cms/components/types'
import { ModalSize } from '../../cms/enums/modal-size'
import { VideoOpenEvent } from '../../cms/services/moving-image/video-events'
import { VideoModalComponent } from '../../cms/components/shared/video-modal/video-modal.component'
import { ImageModalComponent } from '../../cms/components/shared/image-modal/image-modal.component'
import { CampusModalComponent } from '../../cms/components/shared/campus-modal/campus-modal.component'
import { ImageCarouselData } from '../../cms/components/shared/image-carousel-modal/image-carousel-modal.module'
import { ImageCarouselModalComponent } from '../../cms/components/shared/image-carousel-modal/image-carousel-modal.component'
import { DisclaimerData } from '../../cms/components/disclaimer/disclaimer.component.converter'
import { DisclaimerModalComponent } from '../../cms/components/shared/disclaimer-modal/disclaimer-modal.component'
import { ModalImageEvent } from '../../cms/components/shared/gtm-event/image-events'
import { ModalImage } from '../../cms/components/shared/image-modal/image-modal.module'
import { ObjectUtils } from '../utils/object-utils'
import { NavigationStart, Router } from '@angular/router'
import { filter } from 'rxjs/operators'
import { Subscription } from 'rxjs'
import { ConsentModalComponent } from '../../cms/components/shared/consent-modal/consent-modal.component'
import { UcManagementService } from '../../cms/services/usercentrics-consent-manager/uc-management.service'
import { UcService } from '../../cms/enums/uc-service'

@Injectable({
  providedIn: 'root'
})
export class MbModalService implements OnDestroy {

  subscription = new Subscription()
  private modals: NgbModalRef[] = []

  constructor (private ngbModalService: NgbModal,
               private applicationRef: ApplicationRef,
               private events: EventService,
               private router: Router,
               private ucManagementService: UcManagementService) {
    this.subscription.add(this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe(() => this.closeActiveModal()))
  }

  ngOnDestroy () {
    this.subscription.unsubscribe()
  }

  protected get rootComponent () {
    return this.applicationRef?.components?.[0]?.location?.nativeElement
  }

  open (content: any, options?: NgbModalOptions) {
    options = { container: this.rootComponent, ...options }

    const activeModal = this.ngbModalService.open(content, options)
    this.modals.push(activeModal)
    this.handleModalRemoveEvents(activeModal)

    return activeModal
  }

  protected handleModalRemoveEvents (modal: NgbModalRef): void {
    modal.result.finally(() => {
      this.modals = this.modals.filter((m) => m !== modal)
    })
  }

  openVideoModal (containerId: string | undefined, id: string | undefined, video: MiVideo | undefined, caption?: TextInput,
                  description?: TextInput, size?: string) {
    this.events.dispatch(new VideoOpenEvent(video?.title))
    const modal = this.open(VideoModalComponent, {
      ariaDescribedBy: `description-${id}`,
      ariaLabelledBy: `headline-${id}`,
      size: size || ModalSize.XL
    })
    modal.componentInstance.id = id
    modal.componentInstance.containerId = containerId
    modal.componentInstance.video = video
    modal.componentInstance.caption = caption?.value?.trim()?.length ? caption : { value: video?.title }
    modal.componentInstance.description = description?.value?.trim()?.length ? description : { value: video?.description }
    return modal
  }

  openImageModal (id: string | undefined,
                  image: Image | undefined,
                  caption: TextInput | undefined,
                  description: TextInput | undefined,
                  size?: string | undefined) {
    //ToDo: Eventlabel
    this.events.dispatch(
      new ModalImageEvent('Visuals', (caption?.value ? ObjectUtils.getInnerText(caption?.value ?? '') : image?.title ?? ''), true))
    const modalSize = size || ModalSize.MD
    const modal = this.open(ImageModalComponent, {
      ariaDescribedBy: `description-${id}`,
      ariaLabelledBy: `headline-${id}`,
      size: modalSize,
    })
    modal.result.then(() => {
      //ToDo: Eventlabel
      this.events.dispatch(
        new ModalImageEvent('Visuals', caption?.value ? ObjectUtils.getInnerText(caption?.value) : image?.title || '', false))
    })
    modal.componentInstance.id = id
    modal.componentInstance.image = image
    modal.componentInstance.caption = caption
    modal.componentInstance.description = description
    modal.componentInstance.size = modalSize
    return modal
  }

  openCampusModal (id: string | undefined,
                   headline: TextInput | undefined,
                   description: TextInput | undefined,
                   buttonText: string | undefined,
                   modalSize: string | undefined,
                   flexType: string | undefined) {
    const modal = this.open(CampusModalComponent, {
      ariaDescribedBy: `description-${id}`,
      ariaLabelledBy: `headline-${id}`,
      size: modalSize
    })
    modal.componentInstance.id = id
    modal.componentInstance.headline = headline
    modal.componentInstance.description = description
    modal.componentInstance.buttonText = buttonText
    modal.componentInstance.flexType = flexType
    return modal
  }

  openImageCarouselModal (data: ImageCarouselData,
                          id: string | undefined,
                          start: number,
                          size: string = ModalSize.XL,
                          showIndicators?: boolean,
                          endlessScroll?: boolean,
                          useCarouselButtons?: boolean,
                          customClass?: string,
                          backgroundColor?: string
  ) {
    const modal = this.open(ImageCarouselModalComponent, {
      ariaDescribedBy: `description-${id}`,
      ariaLabelledBy: `headline-${id}`,
      size: size,
      modalDialogClass: customClass?.length ? customClass : 'image-carousel-modal',
    })
    modal.componentInstance.data = data
    modal.componentInstance.id = id
    modal.componentInstance.start = start
    modal.componentInstance.size = size
    modal.componentInstance.showIndicators = showIndicators
    modal.componentInstance.endlessScroll = endlessScroll
    modal.componentInstance.useCarouselButtons = useCarouselButtons
    modal.componentInstance.backgroundColor = backgroundColor
    return modal
  }

  openDisclaimerModal (data: DisclaimerData | undefined,
                       url: string | undefined,
                       container?: HTMLElement | undefined,
                       size?: string | undefined) {
    const modal = this.open(DisclaimerModalComponent, {
      ariaDescribedBy: `description-${data?.previewId}`,
      ariaLabelledBy: `headline-${data?.previewId}`,
      container: container,
      size: size || 'md'
    })
    modal.componentInstance.id = data?.previewId
    modal.componentInstance.text = data?.text
    modal.componentInstance.buttonText = data?.buttonText
    modal.componentInstance.url = url
    return modal
  }

  openConsentModal (id: string, service: UcService) {
    const modal = this.open(ConsentModalComponent, {
      ariaDescribedBy: `description-${id}`,
      ariaLabelledBy: `headline-${id}`,
      size: ModalSize.MD
    })
    modal.componentInstance.ucId = id
    modal.componentInstance.ucService = service
    modal.componentInstance.ucServiceId = this.ucManagementService.getServiceId(service)
    return modal
  }

  getModalImageFromImage (picture: Image, size: ModalSize) {
    const image = {
      altText: picture?.altText ?? '',
      mime: picture?.mime ?? '',
      sm: picture?.sm ?? '',
      sm2x: picture?.sm2x ?? '',
      xs: picture?.xs ?? '',
      xs2x: picture?.xs2x ?? ''
    } as ModalImage
    switch (size) {
      case (ModalSize.XL): {
        image.xl = picture?.lg ?? ''
        image.xl2x = picture?.lg2x ?? ''
        image.lg = picture?.lg ?? ''
        image.lg2x = picture?.lg2x ?? ''
        image.md = picture?.md ?? ''
        image.md2x = picture?.md2x ?? ''
        break
      }
      case ModalSize.LG: {
        image.xl = picture?.md ?? ''
        image.xl2x = picture?.md2x ?? ''
        image.lg = picture?.md ?? ''
        image.lg2x = picture?.md2x ?? ''
        image.md = picture?.md ?? ''
        image.md2x = picture?.md2x ?? ''
        break
      }
      case ModalSize.MD: {
        image.xl = picture?.sm ?? ''
        image.xl2x = picture?.sm2x ?? ''
        image.lg = picture?.sm ?? ''
        image.lg2x = picture?.sm2x ?? ''
        image.md = picture?.sm ?? ''
        image.md2x = picture?.sm2x ?? ''
        break
      }
      case ModalSize.SM: {
        image.xl = picture?.xs ?? ''
        image.xl2x = picture?.xs2x ?? ''
        image.lg = picture?.xs ?? ''
        image.lg2x = picture?.xs2x ?? ''
        image.md = picture?.xs ?? ''
        image.md2x = picture?.xs2x ?? ''
        break
      }
    }
    return image
  }

  getActiveModal () {
    const modal = this.modals[this.modals.length - 1]
    return modal ? modal : null
  }

  dismissActiveModal (reason?: any) {
    const modal = this.getActiveModal()

    if (modal) {
      modal.dismiss(reason)
    }
  }

  closeActiveModal (reason?: any) {
    const modal = this.getActiveModal()

    if (modal) {
      modal.close(reason)
      this.modals = []
      try {
        modal.componentInstance.closeDialog(reason)
      } catch {
        modal.close(reason)
      }
    }
  }
}
